import { timelineUseCases } from '@/features/timeline/libs/timeline-services';
import { useRecentFiles } from '@/features/timeline/application/use-timelines';
import type { RecentFileEntry } from '@/features/timeline/application/recent-files';
import { type MouseEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { getIntlLocale } from '@/shared/i18n/use-date-locale';

const formatLastOpened = (value: string): string => {
  return new Intl.DateTimeFormat(getIntlLocale(), {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
};

export const TimelineListPage = () => {
  const navigate = useNavigate();
  const { files, refresh, remove } = useRecentFiles();
  const { t } = useTranslation('timeline');
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    try {
      const created = await timelineUseCases.createTimeline();
      navigate(`/timeline/${created.id}`, {
        state: { initialScale: 'week' as const },
      });
    } catch {
      setError(t('error.createFailed'));
    }
  };

  const handleOpen = async () => {
    try {
      const result = await timelineUseCases.openFile();
      if (!result) return;
      refresh();
      navigate(`/timeline/${result.record.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('error.openFailed'));
    }
  };

  const handleOpenRecent = async (fileName: string) => {
    try {
      // まず保存済みハンドルで直接開く。失敗したらファイルピッカーにフォールバック
      const result = await timelineUseCases.openRecentFile(fileName);
      if (result) {
        refresh();
        navigate(`/timeline/${result.record.id}`);
        return;
      }
      // ハンドルが無い or 権限拒否 → ファイルピッカーで開く
      await handleOpen();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('error.openFailed'));
    }
  };

  return (
    <div className="flex h-screen flex-col" style={{ backgroundColor: 'var(--color-bg-app)' }}>
      <div
        className="flex shrink-0 items-center justify-between gap-3 px-3"
        style={{
          height: 'var(--toolbar-height)',
          backgroundColor: 'var(--color-bg-surface)',
          borderBottom: '1px solid var(--color-border)',
        }}
      >
        <Link to="/" className="flex items-center gap-1.5 no-underline">
          <img src="/favicon.svg" alt="Timeline" className="h-4 w-4" />
          <span
            className="text-xs font-semibold"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            Timeline
          </span>
        </Link>
        <LanguageSwitcher />
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-3xl px-4 py-5">
          <div className="mb-3 flex items-center justify-between">
            <span
              className="text-[11px] font-medium"
              style={{ color: 'var(--color-text-tertiary)' }}
            >
              {files.length > 0 ? t('list.recentFiles') : ''}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="flex items-center gap-1 rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors"
                style={{
                  backgroundColor: 'var(--color-bg-hover)',
                  color: 'var(--color-text-secondary)',
                }}
                onClick={handleOpen}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                </svg>
                {t('file.openFile', { ns: 'common' })}
              </button>
              <button
                type="button"
                className="flex items-center gap-1 rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors"
                style={{
                  backgroundColor: 'var(--color-accent)',
                  color: 'var(--color-accent-fg)',
                }}
                onClick={handleCreate}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                {t('file.new', { ns: 'common' })}
              </button>
            </div>
          </div>

          {error ? (
            <div
              className="mb-3 rounded-lg px-3 py-2 text-[11px]"
              style={{
                backgroundColor: 'var(--color-error-bg)',
                color: 'var(--color-error)',
              }}
            >
              {error}
            </div>
          ) : null}

          {files.length === 0 ? (
            <div
              className="flex flex-col items-center justify-center rounded-lg px-6 py-16 text-center"
              style={{
                border: '1px dashed var(--color-border-strong)',
                backgroundColor: 'var(--color-bg-surface)',
              }}
            >
              <div
                className="flex h-9 w-9 items-center justify-center rounded-lg"
                style={{ backgroundColor: 'var(--color-accent-soft)' }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-accent)" strokeWidth="2" strokeLinecap="round">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <line x1="5" y1="9" x2="5" y2="15" />
                  <line x1="19" y1="9" x2="19" y2="15" />
                </svg>
              </div>
              <p className="mt-3 text-xs font-medium" style={{ color: 'var(--color-text-primary)' }}>
                {t('list.noTimelines')}
              </p>
              <p className="mt-1 text-[11px]" style={{ color: 'var(--color-text-tertiary)' }}>
                {t('list.noTimelinesDesc')}
              </p>
              <div className="mt-4 flex items-center gap-2">
                <button
                  type="button"
                  className="flex items-center gap-1 rounded-md px-3 py-1.5 text-[11px] font-medium transition-colors"
                  style={{
                    backgroundColor: 'var(--color-accent)',
                    color: 'var(--color-accent-fg)',
                  }}
                  onClick={handleCreate}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  {t('file.new', { ns: 'common' })}
                </button>
                <button
                  type="button"
                  className="flex items-center gap-1 rounded-md px-3 py-1.5 text-[11px] font-medium transition-colors"
                  style={{
                    backgroundColor: 'var(--color-bg-hover)',
                    color: 'var(--color-text-secondary)',
                  }}
                  onClick={handleOpen}
                >
                  {t('file.openFile', { ns: 'common' })}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-1">
              {files.map((entry) => (
                <RecentFileRow
                  key={entry.fileName}
                  entry={entry}
                  onOpen={handleOpenRecent}
                  onRemove={remove}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const RecentFileRow = ({
  entry,
  onOpen,
  onRemove,
}: {
  entry: RecentFileEntry;
  onOpen: (fileName: string) => void;
  onRemove: (fileName: string) => void;
}) => {
  const [hovered, setHovered] = useState(false);
  const { t } = useTranslation('timeline');

  return (
    <div
      className="group flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 transition-colors"
      style={{
        backgroundColor: hovered ? 'var(--color-bg-hover)' : 'transparent',
      }}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(entry.fileName)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(entry.fileName);
        }
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md"
        style={{ backgroundColor: 'var(--color-accent-soft)' }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
        </svg>
      </div>

      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-medium" style={{ color: 'var(--color-text-primary)' }}>
          {entry.fileName}
        </p>
        <p className="mt-0.5 text-[10px]" style={{ color: 'var(--color-text-tertiary)' }}>
          {t('list.lastOpened', { date: formatLastOpened(entry.lastOpenedAt) })}
        </p>
      </div>

      <div
        className="flex shrink-0 items-center gap-1"
        style={{ opacity: hovered ? 1 : 0, transition: 'opacity 0.15s ease' }}
      >
        <button
          type="button"
          className="rounded-md px-2 py-1 text-[10px] font-medium transition-colors"
          style={{
            color: 'var(--color-text-tertiary)',
            backgroundColor: 'transparent',
          }}
          onMouseEnter={(e: MouseEvent<HTMLButtonElement>) => {
            e.currentTarget.style.backgroundColor = 'var(--color-bg-active)';
            e.currentTarget.style.color = 'var(--color-error)';
          }}
          onMouseLeave={(e: MouseEvent<HTMLButtonElement>) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = 'var(--color-text-tertiary)';
          }}
          onClick={(e) => {
            e.stopPropagation();
            onRemove(entry.fileName);
          }}
        >
          {t('list.removeFromHistory')}
        </button>
        <div
          className="flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-medium"
          style={{
            color: 'var(--color-accent)',
          }}
        >
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </div>
      </div>
    </div>
  );
};
